/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { TabbedFullWidthAssetItemFragment, TabbedFullWidthAssetSectionFragment } from "@cosmos/hygraph-schema";

import { ResponsiveContainer, SectionContainer, CosmosTabs as Tabs, useTabActiveIndex } from "@components/elements";
import { styled } from "@panda/jsx";
import { vstack } from "@panda/patterns";

import { TabbedFullWidthAssetContent } from "./TabbedFullWidthAsset.Content";
import { HeroAlphaFullWidthImage } from "../../heroes/alpha/HeroAlpha.FullWidthImage";
import { useMemo } from "react";
import { css } from "@panda/css";

const Styled = styled("main", {
	base: {
		display: "block",
		width: "100%",
	},
});

const tabsStyle = css({
	bottom: "eds.dimension.48",
	position: "absolute",
	marginInlineEnd: "eds.dimension.24",
	zIndex: "1",
	smDown: {
		bottom: "eds.dimension.64",
	},
	xsDown: {
		bottom: "eds.dimension.80",
	},
});

const analyticsEventValue = {
	component: "TabbedFullWidthAsset",
	section: "section",
};

export const TabbedFullWidthAssetSection = (
	props: TabbedFullWidthAssetSectionFragment | null,
): React.JSX.Element | null => {
	const tabs = useMemo(() => props?.tabs || [], [props?.tabs]);
	const [activeIndex, setActiveIndex] = useTabActiveIndex(tabs.length);

	// Derive active tab from index
	const activeTab = useMemo(() => tabs[activeIndex] || tabs[0] || {}, [tabs, activeIndex]);

	if (!tabs?.length) return null;

	const enableTabs = tabs.length > 1;

	return (
		<Tabs activeIndex={activeIndex} onActiveIndexChange={(val) => val !== undefined && setActiveIndex(val)} size="lg">
			<Styled className={`tabbed-full-width-asset-section ${vstack({ gap: "12" })}`}>
				<HeroAlphaFullWidthImage heroAsset={activeTab?.heroAsset} />
				<ResponsiveContainer>
					<SectionContainer overflowX="clip" contentVisibility="unset" h="100vh" minHeight="540px">
						{/* Content panels - each tab gets proper ARIA semantics */}
						{tabs.map((tab: TabbedFullWidthAssetItemFragment, index: number) => (
							<Tabs.Panel key={index} index={index}>
								<TabbedFullWidthAssetContent
									heroAsset={tab?.heroAsset}
									eyebrow={tab?.eyebrow}
									title={tab?.title}
									desc={tab?.desc}
									buttons={tab?.buttons || []}
									gradient={tab?.gradient}
								/>
							</Tabs.Panel>
						))}
						{enableTabs ? (
							<Tabs.ButtonList className={tabsStyle}>
								{tabs.map((tab: TabbedFullWidthAssetItemFragment, index: number) => (
									<Tabs.Button
										key={tab.tabLabel || index}
										index={index}
										label={tab.tabLabel ?? ""}
										analytics={{
											eventCategory: "button",
											interactionType: "click",
											eventAction: "select",
											eventLabel: tab.tabLabel ?? null,
											eventValue: analyticsEventValue,
										}}
									/>
								))}
							</Tabs.ButtonList>
						) : null}
					</SectionContainer>
				</ResponsiveContainer>
			</Styled>
		</Tabs>
	);
};
